import './Home.css';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SiteImage from '../components/SiteImage';
import SectionHeading from '../components/SectionHeading';
import RoomCard from '../components/RoomCard';
import GalleryGrid from '../components/GalleryGrid';
import ContactDetails from '../components/ContactDetails';
import BookingPrompt from '../components/BookingPrompt';
import AvailabilityForm from '../components/AvailabilityForm';
import { amenities } from '../data/site';
import { useSiteContent } from '../context/SiteContent';
import '../components/property-overview.css';

export default function Home() {
  const { site } = useSiteContent();
  return (
    <main className="home-page">
      <section className="hero">
        <SiteImage src={site.images.hero} alt="Welcoming guest house setting" className="hero-image" eager />
        <div className="hero-overlay" />
        <motion.div
          className="hero-content page-width"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <span className="eyebrow">A place to pause</span>
          <h1>Your Peaceful Stay<br /><em>Begins Here</em></h1>
          <p>Experience comfort, warmth and hospitality at K and son’s Guest House.</p>
          <AvailabilityForm />
          <span className="hero-note"><SafeIcon name="Sparkles" /> A warm welcome awaits</span>
        </motion.div>
        <div className="hero-scroll" aria-hidden="true"><span /> Discover</div>
      </section>

      <section className="about-preview section-pad page-width">
        <div className="about-photo-wrap">
          <SiteImage src={site.images.about} alt="Comfortable lounge at K and son’s Guest House" className="about-photo" />
        </div>
        <div className="about-copy">
          <span className="eyebrow">Welcome to K and son’s</span>
          <h2>Property Overview</h2>
          <p>This inviting coastal retreat offers the ultimate balance of convenience and relaxation. Situated just 3.5 km from the bustling heart of Negombo and 15 km from Bandaranaike International Airport, the property ensures a seamless arrival and effortless exploration. Guests can enjoy being just a few steps from Negombo Beach and only 1.4 miles from Poruthota Beach, placing golden shores and local culture right at your doorstep.</p>
          <h3>Location Benefits &amp; Highlights</h3>
          <p>Choosing this location provides exceptional convenience, putting world-class dining, historical landmarks, and stunning natural attractions within immediate reach. Whether you are looking to kick off a Sri Lankan vacation with easy airport transit or want to dive straight into local culinary and cultural hotspots, this strategic spot makes exploring effortless.</p>
          <h3>Key Benefits &amp; Nearby Attractions</h3>
          <ul>
            <li><strong>TukTuk Wine &amp; Dine (500 meters away):</strong> Located just a short stroll down St. Joseph Street, this top-rated, vibrant multi-cuisine restaurant and live music venue offers fresh local lagoon seafood, artisanal pizza, and handcrafted cocktails.</li>
            <li><strong>Negombo Beach (Steps away):</strong> Experience instant access to soft sands, gentle waves, and breathtaking sunset views over the Indian Ocean.</li>
            <li><strong>Poruthota Beach (1.4 miles away):</strong> A lively coastal stretch known for its vibrant beach bars, cafes, and friendly local atmosphere.</li>
            <li><strong>St. Mary's Church (Central Negombo):</strong> A stunning 20th-century neoclassical cathedral famous for its magnificent ceilings covered in vivid religious artwork.</li>
            <li><strong>Negombo Dutch Fort:</strong> A historical 17th-century colonial landmark that offers a glimpse into the region's rich heritage.</li>
            <li><strong>Angurukaramulla Temple:</strong> A renowned Buddhist temple featuring an impressive dragon entrance and a massive reclining Buddha statue.</li>
            <li><strong>Hamilton Canal &amp; Negombo Lagoon:</strong> Perfect spots for peaceful boat rides, scenic views of local fishing life, and eco-adventures.</li>
          </ul>
          <Link className="button button-outline" to="/about">
            Learn More <SafeIcon name="ArrowRight" />
          </Link>
        </div>
      </section>

      <section className="rooms-preview section-pad section-cream">
        <div className="page-width">
          <SectionHeading eyebrow="Rest easy" title="Choose Your Room" description="Explore our four comfortable rooms and find the perfect space for your stay." />
          <div className="rooms-grid">{site.rooms.map((room) => <RoomCard room={room} key={room.number} />)}</div>
          <div className="center-action">
            <Link className="text-link" to="/rooms">View all rooms <SafeIcon name="ArrowRight" /></Link>
          </div>
        </div>
      </section>

      <section className="amenities-section section-pad page-width">
        <SectionHeading eyebrow="The little comforts" title="Comfort for Your Stay" description="Enjoy a range of amenities designed to make your visit more comfortable." />
        <div className="amenities-grid">
          {amenities.map((item, index) => (
            <div className="amenity" key={index}>
              <span><SafeIcon name={item.icon.replace('Fi', '')} /></span>
              <p>{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="gallery-preview section-pad section-dark">
        <div className="page-width">
          <SectionHeading eyebrow="Take a look around" title="A Glimpse of Your Stay" description="Take a look at some of the spaces and experiences that make K and son’s Guest House special." light />
          <GalleryGrid compact />
          <div className="center-action">
            <Link className="button button-outline button-outline-light" to="/gallery">
              Explore Gallery <SafeIcon name="ArrowRight" />
            </Link>
          </div>
        </div>
      </section>

      <section className="contact-preview section-pad page-width">
        <div className="contact-map" aria-label="K and son’s Guest House location">
          <iframe
            title="K and son’s Guest House on Google Maps"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d249.3636450214468!2d0!3d0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae2e9500106f0f3%3A0x2a72a669444de185!2sK%20%26%20Sons%20Guest%20house!5e0!3m2!1sen!2ssg!4v1792694982192!5m2!1sen!2ssg"
            width="600"
            height="450"
            style={{ border: 0, width: '100%', height: '100%', minHeight: 390 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
          <a className="button button-outline" href="https://maps.app.goo.gl/rvLZmrNdX4RyBcLr7" target="_blank" rel="noopener noreferrer">
            Get Directions <SafeIcon name="ArrowUpRight" />
          </a>
        </div>
        <div className="contact-copy">
          <span className="eyebrow">We’re here to help</span>
          <h2>Contact Us</h2>
          <p>We’d love to hear from you. Get in touch for enquiries or to plan your stay.</p>
          <ContactDetails />
          <Link className="text-link" to="/contact">More contact details <SafeIcon name="ArrowRight" /></Link>
        </div>
      </section>

      <section className="home-booking-band">
        <div className="page-width">
          <h2>Make time for a little comfort.</h2>
          <BookingPrompt />
        </div>
      </section>
    </main>
  );
}